import { Link } from 'react-router-dom';
import { Mail, Phone, ReceiptText, ShieldCheck, ChevronRight } from 'lucide-react';
import { memberAuth, memberPortal, useAsync, useTenant } from '@fitboxx/shared';
import type { Member } from '@fitboxx/shared';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { initials } from '@/lib/utils';

interface WaiverStatus { requires_signing: boolean; waiver?: { title?: string } }

export default function Me() {
  const { tenant } = useTenant();
  const me = useAsync(() => memberAuth.me() as Promise<Member>);
  const waiver = useAsync(() => memberPortal.waiverStatus() as Promise<WaiverStatus>);

  if (me.loading || !me.data) return <Skeleton className="h-64 w-full" />;
  const m = me.data;
  const fullName = m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">My account</h1>

      <Card className="p-5">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="text-lg">{initials(fullName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-lg font-semibold truncate">{fullName}</div>
            <div className="text-sm text-muted-foreground space-y-0.5 mt-0.5">
              {m.email && <div className="flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{m.email}</div>}
              {m.phone && <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{m.phone}</div>}
            </div>
          </div>
        </div>
      </Card>

      {waiver.data?.requires_signing && (
        <Card className="p-4 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">Action needed: sign waiver</div>
              <div className="text-sm text-amber-800 mt-0.5">
                Please review and sign the {waiver.data.waiver?.title || 'studio waiver'} before your next visit.
              </div>
            </div>
            <Link to="/agreements" className="text-sm font-medium text-amber-900 hover:underline whitespace-nowrap">
              Review →
            </Link>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        <RowLink to="/invoices" icon={ReceiptText} label="Invoices" hint="View and download your receipts" />
        <RowLink to="/agreements" icon={ShieldCheck} label="Agreements" hint="Waivers and consents" />
      </div>

      <div className="text-xs text-muted-foreground text-center pt-4">
        {tenant?.display_name || 'FitBOXX'} · member portal
      </div>
    </div>
  );
}

function RowLink({ to, icon: Icon, label, hint }: { to: string; icon: React.ComponentType<{ className?: string }>; label: string; hint?: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 hover:bg-muted/50 transition">
      <Icon className="h-5 w-5 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}
